/**
 * License detail (Wave-4 console). Opened from the "Manage" action on a license row.
 * Mockup layout: KPI stat cards (seats / devices / validity / tier) → facts
 * (assigned to / status / valid until / issued) → assigned customers → activated
 * devices, with a sticky footer (Edit license → reveals the super-admin edit form;
 * Delete license). Server enforces (006/009).
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import type { DeviceDetail, LicenseDetail } from "@genz/platform";
import { useSessionStore } from "../stores/session.js";
import { ActionBar } from "../ui/ActionBar.js";
import { Drawer } from "../ui/Drawer.js";
import { DeviceDetailPanel } from "./DeviceDetailPanel.js";
import { dedupeDevicesByFingerprint } from "./dedupeDevices.js";
import { RowAction } from "../ui/RowAction.js";
import {
  DeviceTable,
  FactRows,
  LicenseEditForm,
  LicenseRenewControls,
  SectionLabel,
  StatCards,
  StatusBadge,
  fmtDate,
} from "./detail-shared.js";

export function LicenseDetailPanel({
  licenseId,
  onChanged,
  onDeleted,
}: {
  licenseId: string;
  onChanged?: () => void;
  onDeleted?: () => void;
}) {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [detail, setDetail] = useState<LicenseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [assignId, setAssignId] = useState("");
  const [edit, setEdit] = useState(false);
  const [deviceFor, setDeviceFor] = useState<DeviceDetail | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await client.licenseDetail(licenseId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, licenseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function assign(e: React.FormEvent) {
    e.preventDefault();
    if (!assignId.trim()) return;
    setBusy("assign");
    setError(null);
    try {
      setDetail(await client.assignLicense(licenseId, assignId.trim()));
      setAssignId("");
      onChanged?.();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(null);
    }
  }

  async function unassign(customerId: string) {
    setBusy(`unassign-${customerId}`);
    setError(null);
    try {
      setDetail(await client.unassignLicense(licenseId, customerId));
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (typeof window !== "undefined" &&
        !window.confirm("Delete this license and all its activations & devices? This cannot be undone.")) {
      return;
    }
    setBusy("delete");
    setError(null);
    try {
      await client.deleteLicense(licenseId);
      onDeleted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (loading && !detail) {
    return <p className="faint" data-testid="license-detail-loading">Loading license…</p>;
  }
  if (!detail) {
    return (
      <div data-testid="license-detail-empty">
        <p role="alert" className="faint">{error ?? "Couldn’t load this license."}</p>
        <button type="button" className="btn btn--sm" onClick={() => void refresh()}>Retry</button>
      </div>
    );
  }

  // Collapse session rows to one entry per physical device (fingerprint) so a single
  // machine isn't listed (or counted) 2–3× — see dedupeDevicesByFingerprint.
  const devices = dedupeDevicesByFingerprint(detail.devices, (d) => `${d.customer_id ?? ""}::${d.fingerprint}`);

  return (
    /* The bar is a SIBLING of `.detail` (Z0 fix round P-2): `.drawer__body` is a scrolling
       column with rule 7's sticky bar over it, and the shared clearance comes from
       `:where(:has(> .actionbar))`, which only matches a DIRECT child. */
    <>
    <div className="detail" data-testid="license-detail">
      <StatCards
        cards={[
          { label: "Seats", value: `${detail.seats_used} / ${detail.seats}`, sub: "in use", testid: "license-kpi-seats" },
          {
            label: "Devices",
            value: detail.device_cap != null ? `${devices.length} / ${detail.device_cap}` : `${devices.length}`,
            sub: detail.device_cap != null ? "active" : "tier default",
            testid: "license-kpi-devices",
          },
          { label: "Validity", value: detail.expires_at ? fmtDate(detail.expires_at) : "Perpetual", testid: "license-kpi-validity" },
          { label: "Tier", value: detail.tier, testid: "license-kpi-tier" },
        ]}
      />

      <FactRows
        testid="license-facts"
        rows={[
          { label: "Assigned to", value: `${detail.assignment_count} customer${detail.assignment_count === 1 ? "" : "s"}` },
          { label: "Status", value: <StatusBadge status={detail.expired ? "expired" : "active"} /> },
          { label: "Valid until", value: detail.expires_at ? fmtDate(detail.expires_at) : "Perpetual" },
          { label: "Issued", value: fmtDate(detail.created_at) },
        ]}
      />

      {edit && isSuper && (
        <div className="inspector" data-testid="license-edit-inset">
          <div className="inspector__head">Edit license</div>
          <div className="inspector__body">
            {/* The drawer's commit, so it is the drawer's one primary (rule 7). */}
            <LicenseEditForm emphasis="primary" license={detail} onSaved={(next) => { setDetail(next); onChanged?.(); }} />
            <LicenseRenewControls license={detail} onSaved={(next) => { setDetail(next); onChanged?.(); }} />
          </div>
        </div>
      )}

      <SectionLabel>Assigned customers ({detail.assignments.length})</SectionLabel>
      {detail.assignments.length === 0 ? (
        <p className="faint" data-testid="license-no-assignments">No customers assigned yet.</p>
      ) : (
        <div className="table-scroll">
        <table className="table" data-testid="license-assignments">
          <thead>
            <tr><th>Customer</th><th>Activation</th><th className="num">Devices</th>{isSuper && <th />}</tr>
          </thead>
          <tbody>
            {detail.assignments.map((a) => (
              <tr key={a.customer_id} data-testid="license-assignment-row">
                <td>{a.email ?? a.customer_id}</td>
                <td><StatusBadge status={a.activation_status} /></td>
                <td className="num">{a.device_count}</td>
                {isSuper && (
                  <td>
                    <RowAction
                      label="Remove"
                      icon="minus-circle"
                      tone="quiet"
                      disabled={busy === `unassign-${a.customer_id}`}
                      busy={busy === `unassign-${a.customer_id}`}
                      onClick={() => void unassign(a.customer_id)}
                      data-testid="license-unassign"
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {isSuper && (
        <form className="admin-tab__form" onSubmit={assign} data-testid="license-assign-form">
          <div className="field">
            <label htmlFor={`lic-assign-${licenseId}`}>Add a customer</label>
            <input
              id={`lic-assign-${licenseId}`}
              type="text"
              value={assignId}
              onChange={(e) => setAssignId(e.target.value)}
              placeholder="customer id"
              data-testid="license-assign-id"
            />
          </div>
          <button type="submit" className="btn btn--sm" disabled={!assignId.trim() || busy === "assign"} data-testid="license-assign-submit">
            {busy === "assign" ? "Adding…" : "Assign"}
          </button>
        </form>
      )}

      <SectionLabel>Activated devices ({devices.length})</SectionLabel>
      {/* WS-Z9: the shared `DeviceTable` — the same one the customer drawer mounts. Two card
          lists carrying `data-testid="detail-devices"` on two different shapes is a testid
          that means nothing; there is one shape now. */}
      <DeviceTable
        devices={devices}
        onManage={(d) => setDeviceFor(d)}
        manageTestId="license-device-manage"
      />

      {error && <p role="alert" data-testid="license-detail-error">{error}</p>}
    </div>

      {isSuper && (
        /* Rule 7. "Edit license" was a `.btn--primary` DISCLOSURE TOGGLE — the loudest
           control on the drawer opened a panel rather than committing anything, and then sat
           beside the real Save shouting louder than it. The bar's actions are both
           secondary; the one primary appears inside the editor when the editor is open. */
        <ActionBar label="License actions">
          <button
            type="button"
            className="btn"
            onClick={() => setEdit((v) => !v)}
            data-testid="license-edit-toggle"
            aria-expanded={edit}
          >
            {edit ? "Close editor" : "Edit license"}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy === "delete"}
            onClick={() => void remove()}
            data-testid="license-delete"
          >
            {busy === "delete" ? "Deleting…" : "Delete license"}
          </button>
        </ActionBar>
      )}

      {deviceFor && (
        <Drawer
          title={deviceFor.label ?? deviceFor.device_id}
          subtitle="Device"
          badge={<StatusBadge status={deviceFor.active ? "active" : "grace_lapsed"} />}
          onClose={() => setDeviceFor(null)}
          testid="device-detail-drawer"
        >
          <DeviceDetailPanel
            device={deviceFor}
            onChanged={() => void refresh()}
            onClose={() => setDeviceFor(null)}
          />
        </Drawer>
      )}
    </>
  );
}
